import { PERSONAL_THRESHOLDS, OVERFLOW_THRESHOLDS } from '@/constants/thresholds'
import { formatCurrency, formatPercentage, getPercentage } from '@/utils/formatters'

interface CapProgressBarProps {
  used: number
  total: number
  type: 'personal' | 'overflow'
  label?: string
  visible?: boolean
  /** Others' usage — renders a red segment from the right (sole-owner geos only) */
  othersUsed?: number
  /** When set, shows this as the cap amount in labels (reduced cap after others' usage) */
  effectiveTotal?: number
}

function getBarColor(ratio: number, type: 'personal' | 'overflow'): { bg: string; hex: string; label: string } {
  const thresholds = type === 'personal' ? PERSONAL_THRESHOLDS : OVERFLOW_THRESHOLDS
  for (const t of thresholds) {
    if (ratio <= t.max) return t
  }
  return thresholds[thresholds.length - 1]
}

export function CapProgressBar({ used, total, type, label, visible = true, othersUsed, effectiveTotal }: CapProgressBarProps) {
  if (!visible) return null

  // Display values use effectiveTotal (reduced cap) when available
  const displayTotal = effectiveTotal ?? total
  const ratio = displayTotal > 0 ? used / displayTotal : 0
  const { bg, hex } = getBarColor(ratio, type)

  // Bar widths are relative to the original total (full cap)
  const usedPct = total > 0 ? getPercentage(used, total) : 0
  const othersPct = othersUsed && total > 0
    ? Math.min(100, Math.round((othersUsed / total) * 100))
    : 0

  const defaultLabel = `${formatCurrency(used)} / ${formatCurrency(displayTotal)}`

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-text-muted">
          {label ?? (type === 'personal' ? 'My Cap' : 'Global Cap')}
        </span>
        <div className="flex items-baseline gap-2">
          <span className="text-xs text-text-tertiary">
            {label ? '' : defaultLabel}
          </span>
          <span className="text-xs font-semibold" style={{ color: hex }}>
            {formatPercentage(used, displayTotal)}
          </span>
        </div>
      </div>

      <div className="relative h-2 bg-gray-100 rounded-full overflow-hidden">
        {/* My usage — green from left */}
        <div
          className={`absolute inset-y-0 left-0 rounded-full transition-all duration-700 ease-out ${bg}`}
          style={{ width: `${Math.min(100, usedPct)}%` }}
        />
        {/* Others' usage — red from right */}
        {othersPct > 0 && (
          <div
            className="absolute inset-y-0 right-0 rounded-full bg-red-300 transition-all duration-700 ease-out"
            style={{ width: `${othersPct}%` }}
          />
        )}
      </div>

      {othersPct > 0 && othersUsed != null && (
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-red-400">
            Overflow full
          </span>
        </div>
      )}
    </div>
  )
}
